import React, { useEffect, useRef, useState } from "react";

import GoogleMapReact from "google-map-react";

import { checkExperienceCookieConsent, getGoogleKey, matchGeoFirstAndLastCoords, useCookieConsent } from "utils";

import { Coordinates } from "models";

import { MapCookieConsentReminder } from "components";

const DEFAULT_CENTER: Coordinates = { lat: 0, lng: 0 };

type MapBoundaryCompareProps = {
  /** First boundary — rendered as a solid outline with no fill. */
  primaryGeometry: Coordinates[] | null;
  /** Second boundaries — each rendered as a dashed, filled outline, drawn on top of the primary one. */
  secondaryGeometries?: Coordinates[][] | null;
  /** Fallback center used before a geometry is available to fit bounds around. */
  location?: Coordinates | null;
  /** `primaryGeometry`'s stroke color (standard CSS hex, e.g. "#1A73E8"). Defaults to white. */
  primaryColor?: string;
  /** `secondaryGeometries`' stroke/fill color (standard CSS hex, e.g. "#FF8C00"). Defaults to white. */
  secondaryColor?: string;
  /** Show native zoom control and enable scroll-to-zoom. */
  zoomable?: boolean;
  /** Let the viewer drag to pan the map. */
  pannable?: boolean;
  /** Padding (px) passed to `map.fitBounds`. */
  boundsPadding?: number;
  /** Stroke weight for the primary boundary's solid outline. */
  primaryWeight?: number;
  /** Stroke weight for the secondary boundary's dashed outline. */
  dashWeight?: number;
  /** Icon scale for the secondary boundary's dash marks — larger reads as a thicker dashed line. */
  dashScale?: number;
};

/**
 * Compares two field boundaries on an interactive Google Map: a solid outline against a dashed,
 * filled outline drawn on top of it. Google's Static Maps API (used by `MapStatic`) has no dashed-line
 * option, so this uses the Maps JavaScript API instead via `google-map-react`, the same library already
 * used by `MapInteractive`/`useMapPolygons` for imperative polygon overlays.
 */
export const MapBoundaryCompare = ({
  primaryGeometry,
  secondaryGeometries,
  location,
  primaryColor = "#FFFFFF",
  secondaryColor = "#FFFFFF",
  zoomable = false,
  pannable = false,
  boundsPadding = 40,
  primaryWeight = 2,
  dashWeight = 3,
  dashScale = 4,
}: MapBoundaryCompareProps) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [maps, setMaps] = useState<typeof google.maps | null>(null);
  const overlaysRef = useRef<(google.maps.Polygon | google.maps.Polyline)[]>([]);
  const hasCookieConsent = useCookieConsent(checkExperienceCookieConsent);

  useEffect(() => {
    if (!map || !maps) return;

    overlaysRef.current.forEach((overlay) => overlay.setMap(null));
    overlaysRef.current = [];

    const secondaryRings = (secondaryGeometries ?? []).filter((ring): ring is Coordinates[] => !!ring?.length);

    if (!primaryGeometry?.length && !secondaryRings.length) return;

    const bounds = new maps.LatLngBounds();

    if (primaryGeometry?.length) {
      const polygon = new maps.Polygon({
        paths: matchGeoFirstAndLastCoords(primaryGeometry),
        strokeColor: primaryColor,
        strokeWeight: primaryWeight,
        fillOpacity: 0,
        clickable: false,
        map,
      });
      overlaysRef.current.push(polygon);
      primaryGeometry.forEach((coord) => bounds.extend(coord));
    }

    secondaryRings.forEach((ring) => {
      const closedSecondary = matchGeoFirstAndLastCoords(ring);
      const fill = new maps.Polygon({
        paths: closedSecondary,
        strokeOpacity: 0,
        fillColor: secondaryColor,
        fillOpacity: 0.2,
        clickable: false,
        map,
      });
      const dashedOutline = new maps.Polyline({
        path: closedSecondary,
        strokeOpacity: 0,
        clickable: false,
        icons: [
          {
            icon: { path: "M 0,-1 0,1", strokeOpacity: 1, strokeColor: secondaryColor, strokeWeight: dashWeight, scale: dashScale },
            offset: "0",
            repeat: "16px",
          },
        ],
        map,
      });
      overlaysRef.current.push(fill, dashedOutline);
      ring.forEach((coord) => bounds.extend(coord));
    });

    if (!bounds.isEmpty()) map.fitBounds(bounds, boundsPadding);

    return () => {
      overlaysRef.current.forEach((overlay) => overlay.setMap(null));
      overlaysRef.current = [];
    };
  }, [map, maps, primaryGeometry, secondaryGeometries, boundsPadding, primaryColor, secondaryColor, primaryWeight, dashWeight, dashScale]);

  if (!hasCookieConsent) {
    return <MapCookieConsentReminder />;
  }

  return (
    <GoogleMapReact
      bootstrapURLKeys={{ key: getGoogleKey() }}
      defaultCenter={DEFAULT_CENTER}
      center={location ?? DEFAULT_CENTER}
      defaultZoom={15}
      options={(maps: typeof google.maps) => ({
        mapTypeId: maps.MapTypeId.SATELLITE,
        zoomControl: zoomable,
        scrollwheel: zoomable,
        draggable: pannable,
        disableDefaultUI: true,
        clickableIcons: false,
      })}
      yesIWantToUseGoogleMapApiInternals
      onGoogleApiLoaded={({ map, maps }: { map: google.maps.Map; maps: typeof google.maps }) => {
        setMap(map);
        setMaps(maps);
      }}
    />
  );
};

export default MapBoundaryCompare;
